import React, { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import MotionSection from "./MotionSection";
import { Button } from "../components/ui";
import {
  BUTTON_VARIANTS,
  HERO_MODIFIER,
  LEGACY_FORKS,
  STATUS_META,
} from "./connectionMap";
import ComponentEntry from "./ComponentEntry";
import { COMPONENT_REGISTRY } from "./componentRegistry";
import "./style-guide.scss";

const SIZES = ["sm", "md", "lg", "xl", "2xl"];

// Live token swatches — read straight from design-tokens.css via CSS vars so
// this reflects whatever the tokens currently resolve to.
const COLOR_TOKENS = [
  "--bg-primary", "--bg-secondary", "--bg-tertiary",
  "--purple-primary", "--purple-soft",
  "--text-light", "--text-soft", "--text-muted",
  "--border-primary", "--danger", "--success",
];
const CONTROL_TOKENS = ["--control-sm", "--control-md", "--control-lg", "--control-xl", "--control-2xl"];
const RADIUS_TOKENS = ["--radius-sm", "--radius-md", "--radius-lg", "--radius-pill"];

const StatusTag = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.unused;
  return (
    <span className={`sg-tag sg-tag--${meta.tone}`} title={meta.hint}>
      {meta.label}
    </span>
  );
};

// Reusable "which files is this wired into" disclosure.
const Connections = ({ count, sites }) => {
  const [open, setOpen] = useState(false);
  // Auto-animate the disclosure: the file list springs open/closed for free
  // when `open` toggles the child in and out. First live use of auto-animate.
  const [listRef] = useAutoAnimate();
  if (!count) return <span className="sg-conn sg-conn--none">0 connections</span>;
  return (
    <div className="sg-conn" ref={listRef}>
      <button type="button" className="sg-conn__toggle" onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} {count} connection{count !== 1 ? "s" : ""}
      </button>
      {open && (
        <ul className="sg-conn__list">
          {sites.map((f) => (
            <li key={f}><code>{f}</code></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Section = ({ id, title, children, blurb }) => (
  <MotionSection className="sg-section" id={id}>
    <h2 className="sg-section__title">{title}</h2>
    {blurb && <p className="sg-section__blurb">{blurb}</p>}
    {children}
  </MotionSection>
);

const SG_THEMES = [
  { id: "glass", label: "Luxe glass" },
  { id: "editorial", label: "Bold editorial" },
  { id: "gradient", label: "Vibrant gradient" },
];

const StyleGuidePage = () => {
  const [sgTheme, setSgTheme] = useState("glass");
  const reduce = useReducedMotion();

  // Signature entrance: the header block eases in on load and its children
  // stagger. Reduced-motion collapses the variants to a no-op so content is
  // instant and static. Spring throughout, per the master-frontend Motion phase.
  const headContainer = reduce
    ? {}
    : {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
      };
  const headItem = reduce
    ? {}
    : {
        hidden: { opacity: 0, y: 16 },
        show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 26 } },
      };

  return (
    <div className="sg" data-sg-theme={sgTheme}>
      {/* Direction switcher — flip between the three candidate looks live. */}
      <div className="sg-switch" role="tablist" aria-label="Design direction">
        <span className="sg-switch__label">Direction</span>
        {SG_THEMES.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={sgTheme === t.id}
            className={`sg-switch__btn${sgTheme === t.id ? " sg-switch__btn--active" : ""}`}
            onClick={() => setSgTheme(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <motion.header
        className="sg-head"
        variants={headContainer}
        initial={reduce ? false : "hidden"}
        animate={reduce ? false : "show"}
      >
        <motion.span className="sg-eyebrow" variants={headItem}>
          Retail Circle · Design System
        </motion.span>
        <motion.h1 variants={headItem}>
          The parts every screen is<br />built from.
        </motion.h1>
        <motion.p variants={headItem}>
          Every shared primitive rendered from the real components and tokens, with the
          count of screens each one feeds. Change a part here and it changes everywhere —
          this page updates as you edit.
        </motion.p>
        <motion.nav className="sg-nav" variants={headItem}>
          <a href="#tokens">Tokens</a>
          <a href="#buttons">Buttons</a>
          <a href="#hero">Hero CTA</a>
          <a href="#components">Components</a>
          <a href="#legacy">Legacy forks</a>
        </motion.nav>
        <motion.div className="sg-legend" variants={headItem}>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <span key={k} className="sg-legend__item">
              <span className={`sg-tag sg-tag--${m.tone}`}>{m.label}</span>
              <span className="sg-legend__hint">{m.hint}</span>
            </span>
          ))}
        </motion.div>
      </motion.header>

      {/* ── TOKENS ─────────────────────────────────────────────── */}
      <Section id="tokens" title="Design tokens" blurb="Read live from design-tokens.css.">
        <h3 className="sg-sub">Colour</h3>
        <div className="sg-swatches">
          {COLOR_TOKENS.map((t) => (
            <div key={t} className="sg-swatch">
              <span className="sg-swatch__chip" style={{ background: `var(${t})` }} />
              <code>{t}</code>
            </div>
          ))}
        </div>

        <h3 className="sg-sub">Control heights</h3>
        <div className="sg-bars">
          {CONTROL_TOKENS.map((t) => (
            <div key={t} className="sg-bar">
              <span className="sg-bar__fill" style={{ height: `var(${t})` }} />
              <code>{t}</code>
            </div>
          ))}
        </div>

        <h3 className="sg-sub">Radius</h3>
        <div className="sg-swatches">
          {RADIUS_TOKENS.map((t) => (
            <div key={t} className="sg-swatch">
              <span className="sg-swatch__chip sg-swatch__chip--radius" style={{ borderRadius: `var(${t})` }} />
              <code>{t}</code>
            </div>
          ))}
        </div>
      </Section>

      {/* ── BUTTONS ────────────────────────────────────────────── */}
      <Section
        id="buttons"
        title="Button — variants × sizes"
        blurb="Each variant rendered at every size, with how many call sites use it."
      >
        {BUTTON_VARIANTS.map((v) => (
          <div key={v.variant} className="sg-row">
            <div className="sg-row__head">
              <code className="sg-row__name">variant="{v.variant}"</code>
              <StatusTag status={v.status} />
              <Connections count={v.count} sites={v.sites} />
            </div>
            {v.note && <p className="sg-row__note">{v.note}</p>}
            <div className="sg-specimens">
              {SIZES.map((s) => (
                <Button key={s} variant={v.variant} size={s}>
                  {v.variant} {s}
                </Button>
              ))}
              <Button variant={v.variant} size="md" disabled>disabled</Button>
            </div>
          </div>
        ))}
      </Section>

      {/* ── HERO CTA ───────────────────────────────────────────── */}
      <Section
        id="hero"
        title="CTA hero button"
        blurb={HERO_MODIFIER.note}
      >
        <div className="sg-row">
          <div className="sg-row__head">
            <code className="sg-row__name">.{HERO_MODIFIER.name}</code>
            <StatusTag status={HERO_MODIFIER.status} />
            <Connections count={HERO_MODIFIER.sites.length} sites={HERO_MODIFIER.sites} />
          </div>
          <div className="sg-specimens">
            <Button variant="primary" size="lg" className="rc-ui-btn--hero">+ New Listing</Button>
            <Button variant="primary" size="lg" className="rc-ui-btn--hero">+ New Lookbook</Button>
          </div>
        </div>
      </Section>

      {/* ── COMPONENTS (registry-driven, one mould) ────────────────
          Every entry is a ComponentEntry fed from componentRegistry.jsx. To
          document a new component: add ONE object to that registry — same
          shape gives it a live demo, copy-paste snippet, props table + guidance.
      */}
      <Section
        id="components"
        title="Components — the reusable reference"
        blurb="Each rendered from the real component with a copy-paste snippet, props and do/don't. Add a new one by copying an entry in componentRegistry.jsx."
      >
        {COMPONENT_REGISTRY.map((c) => (
          <ComponentEntry key={c.name} {...c}>
            {c.demo}
          </ComponentEntry>
        ))}
      </Section>

      {/* ── LEGACY FORKS ───────────────────────────────────────── */}
      <Section
        id="legacy"
        title="Legacy forks — outside the design system"
        blurb="Same job as a DS primitive, but never adopted it. Migrate these to change behaviour in one place."
      >
        <table className="sg-table">
          <thead>
            <tr><th>Fork</th><th>Where</th><th>Does the job of</th><th>Note</th></tr>
          </thead>
          <tbody>
            {LEGACY_FORKS.map((f) => (
              <tr key={f.name}>
                <td><code>{f.name}</code> <StatusTag status="legacy" /></td>
                <td><code>{f.where}</code></td>
                <td>{f.doesJobOf}</td>
                <td>{f.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

export default StyleGuidePage;
